import React, { Fragment, useEffect, useState } from 'react';
import oneAppModuleWrapper from '@americanexpress/one-app-module-wrapper';
import { IntlProvider } from 'react-intl';
import { loadLanguagePack } from '@americanexpress/one-app-ducks';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { ProgressCircular } from '@americanexpress/dls-react';
import { queryModule } from 'iguazu-holocron';
import { compose } from 'redux';
import { connectAsync } from 'iguazu';
import SharedScript from '@americanexpress/shared-scripts';
import childRoutes from '../childRoutes';

const AMEX_SESSION_DELAY_QUERY_PARAM = 'reproAmexSessionDelayMs';
const MAX_AMEX_SESSION_DELAY_MS = 120000;

const getAmexSessionDelayMs = () => {
  if (typeof window === 'undefined') return 0;

  const requestedDelay = Number(
    new URLSearchParams(window.location.search).get(AMEX_SESSION_DELAY_QUERY_PARAM)
  );

  if (!Number.isFinite(requestedDelay) || requestedDelay <= 0) return 0;
  return Math.min(requestedDelay, MAX_AMEX_SESSION_DELAY_MS);
};

if (typeof document !== 'undefined') {
  document.title = 'Virtual Cards';
}

const VirtualCardsRoot = ({
  languageData, locale, children,
  GlobalHeader, GlobalFooter,
}) => {
  const [amexSessionDelayMs] = useState(getAmexSessionDelayMs);
  const [shouldLoadAmexSession, setShouldLoadAmexSession] = useState(
    amexSessionDelayMs === 0
  );

  useEffect(() => {
    if (amexSessionDelayMs === 0) return undefined;

    const timeoutId = setTimeout(() => {
      setShouldLoadAmexSession(true);
    }, amexSessionDelayMs);

    return () => clearTimeout(timeoutId);
  }, [amexSessionDelayMs]);

  if (Object.entries(languageData).length === 0) {
    return <ProgressCircular />;
  }

  return (
    <Fragment>
      {shouldLoadAmexSession && (
        <SharedScript
          name="one-identity-session"
          version="^1.0.0"
          windowCheck="AmexSession"
        />
      )}
      <IntlProvider locale={locale} messages={languageData}>
        <Helmet
          htmlAttributes={{ lang: locale }}
          title={languageData['helmet.title']}
          meta={[
            {
              name: 'viewport',
              content: 'width=device-width, initial-scale=1, maximum-scale=5',
            },
          ]}
          link={[
            {
              rel: 'stylesheet',
              href: 'https://www.aexp-static.com/cdaas/dls/packages/@americanexpress/dls/7.16.0/stylesheets/dls-max.min.css',
            },
          ]}
        />
        {children && React.cloneElement(children, {
          GlobalHeader,
          GlobalFooter,
        })}
      </IntlProvider>
    </Fragment>
  );
};

export const TestableVirtualCardsRoot = VirtualCardsRoot;

VirtualCardsRoot.propTypes = {
  languageData: PropTypes.objectOf(PropTypes.string).isRequired,
  locale: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  GlobalHeader: PropTypes.elementType.isRequired,
  GlobalFooter: PropTypes.elementType.isRequired,
};

// Read about childRoutes:
// https://github.com/americanexpress/one-app/blob/main/docs/api/modules/Routing.md#childroutes
VirtualCardsRoot.childRoutes = childRoutes;

// Read about appConfig:
// https://github.com/americanexpress/one-app/blob/main/docs/api/modules/App-Configuration.md
/* istanbul ignore next */
if (!global.BROWSER) {
  // eslint-disable-next-line global-require -- require needs to be inside browser check
  VirtualCardsRoot.appConfig = require('../appConfig').default;
}

export const loadModuleData = ({ store: { dispatch } }) => {
  dispatch(loadLanguagePack('virtual-cards-root', { fallbackLocale: 'en-US' }));
};

export const loadDataAsProps = ({ store: { dispatch } }) => ({
  GlobalHeader: () => dispatch(queryModule('axp-global-header')),
  GlobalFooter: () => dispatch(queryModule('axp-footer')),
});

VirtualCardsRoot.holocron = {
  name: 'virtual-cards-root',
  loadModuleData,
};

export default compose(
  oneAppModuleWrapper('virtual-cards-root'),
  connectAsync({ loadDataAsProps })
)(VirtualCardsRoot);
